import { getAllNomineesByEdition, type NomineeDetailResponse } from '@api/nominees-api';
import Breadcrumbs from '@components/breadcrumbs';
import Modal from '@components/modal';
import TableFilters from '@components/table-filters';
import EditNomineeModal from '@features/nominees/edit-nominee-modal';
import { useAuth } from '@hooks/use-auth';
import { useCategories } from '@hooks/use-categories';
import { useEdition } from '@hooks/use-edition';
import { useNominees } from '@hooks/use-nominees';
import { getAvatarColor, getInitials } from '@utils/avatar-utils';
import { downloadCsv } from '@utils/csv-utils';
import {
  Award,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleSlash,
  Download,
  ExternalLink,
  ListFilter,
  MapPin,
  Pencil,
  Stethoscope,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { sileo } from 'sileo';

import {
  buildEditionNomineesCsv,
  getEditionNomineesCsvFilename,
  type NomineeTypeFilter,
  type NomineeVotedFilter,
} from './edition-nominees-export';

const PAGE_SIZE = 12;
const ALL_CATEGORIES = 'all';

function getPositionBadge(position: number): string {
  if (position === 1) return 'edition-nominee__pos--gold';
  if (position === 2) return 'edition-nominee__pos--silver';
  if (position === 3) return 'edition-nominee__pos--bronze';
  return 'edition-nominee__pos--default';
}

function getPageRange(currentPage: number, totalPages: number): number[] {
  const MAX_VISIBLE = 7;

  if (totalPages <= MAX_VISIBLE) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: number[] = [1];

  let start = Math.max(2, currentPage - 1);
  let end = Math.min(totalPages - 1, currentPage + 1);

  if (currentPage <= 3) {
    start = 2;
    end = 5;
  } else if (currentPage >= totalPages - 2) {
    start = totalPages - 4;
    end = totalPages - 1;
  }

  if (start > 2) pages.push(-1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push(-2);

  pages.push(totalPages);
  return pages;
}

interface GroupedNominee {
  sourceNominee: NomineeDetailResponse;
  participantId: string;
  name: string;
  surname: string | null;
  city: string | null;
  photoUrl: string | null;
  profileUrl: string | null;
  totalVotes: number;
  bestRanking: number | null;
  categories: {
    editionCategoryId: string;
    categoryId: string;
    categoryName: string;
    voteCount: number;
    ranking: number | null;
  }[];
}

interface CategoryOption {
  readonly id: string;
  readonly name: string;
}

function getCategoryLocalName(category: { name: string; details: { name: string }[] }): string {
  return category.details[0]?.name ?? category.name;
}

export default function EditionNominees(): React.JSX.Element {
  const { editionId } = useParams<{ editionId: string }>();
  const { isAdmin } = useAuth();
  const { edition } = useEdition(editionId);
  const { nominees, isLoading, refetch } = useNominees(editionId);
  const { categories: editionCategories } = useCategories(editionId);
  const editionLabel = edition ? `${edition.country} ${edition.year}`.trim() : 'Edition';

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(ALL_CATEGORIES);
  const [nameSearch, setNameSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingNominee, setEditingNominee] = useState<NomineeDetailResponse | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportNomineeType, setExportNomineeType] = useState<NomineeTypeFilter>('all');
  const [exportVotedStatus, setExportVotedStatus] = useState<NomineeVotedFilter>('all');
  const [isExporting, setIsExporting] = useState(false);

  const availableCategories = useMemo<CategoryOption[]>(
    () =>
      editionCategories
        .map((ec) => ({ id: ec.category.id, name: getCategoryLocalName(ec.category) }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [editionCategories],
  );

  const filteredNominees = useMemo(
    () =>
      selectedCategoryId === ALL_CATEGORIES
        ? nominees
        : nominees.filter((n) => n.category.id === selectedCategoryId),
    [nominees, selectedCategoryId],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, GroupedNominee>();

    for (const n of filteredNominees) {
      const pid = n.participant.id;
      const existing = map.get(pid);
      const catEntry = {
        editionCategoryId: n.editionCategoryId,
        categoryId: n.category.id,
        categoryName: n.category.name,
        voteCount: n.voteCount,
        ranking: n.ranking,
      };

      if (existing) {
        existing.totalVotes += n.voteCount;
        existing.categories.push(catEntry);
        if (n.ranking !== null) {
          existing.bestRanking =
            existing.bestRanking === null ? n.ranking : Math.min(existing.bestRanking, n.ranking);
        }
      } else {
        map.set(pid, {
          sourceNominee: n,
          participantId: pid,
          name: n.participant.name,
          surname: n.participant.surname,
          city: n.participant.city,
          photoUrl: n.participant.photoUrl,
          profileUrl: n.participant.profileUrl,
          totalVotes: n.voteCount,
          bestRanking: n.ranking,
          categories: [catEntry],
        });
      }
    }

    const lowerSearch = nameSearch.toLowerCase().trim();
    const values = Array.from(map.values());
    const searched = lowerSearch
      ? values.filter((g) => {
          const fullName = [g.name, g.surname].filter(Boolean).join(' ').toLowerCase();
          return fullName.includes(lowerSearch);
        })
      : values;

    return searched.sort((a, b) => b.totalVotes - a.totalVotes);
  }, [filteredNominees, nameSearch]);

  const totalPages = Math.ceil(grouped.length / PAGE_SIZE);
  const paginatedNominees = useMemo(
    () => grouped.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [grouped, currentPage],
  );

  const uniqueParticipants = grouped.length;
  const uniqueCategoryCount = useMemo(
    () => new Set(filteredNominees.map((n) => n.category.id)).size,
    [filteredNominees],
  );
  const hasNoNominees = !isLoading && nominees.length === 0;

  const handleCategoryChange = (categoryId: string): void => {
    setSelectedCategoryId(categoryId);
    setCurrentPage(1);
  };

  const handleNameSearchChange = (value: string): void => {
    setNameSearch(value);
    setCurrentPage(1);
  };

  const handleExport = async (): Promise<void> => {
    if (!editionId) return;

    setIsExporting(true);
    try {
      const allNominees = await getAllNomineesByEdition(editionId);
      const csv = buildEditionNomineesCsv(allNominees, {
        nomineeType: exportNomineeType,
        votedStatus: exportVotedStatus,
      });
      downloadCsv(csv, getEditionNomineesCsvFilename(editionId, edition?.year));
      sileo.success({ title: 'Nominees CSV exported successfully' });
      setIsExportModalOpen(false);
    } catch {
      sileo.error({ title: 'Failed to export nominees CSV' });
    } finally {
      setIsExporting(false);
    }
  };

  const startIndex = (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, uniqueParticipants);

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Editions', to: '/editions' },
          { label: editionLabel },
          { label: 'All nominees' },
        ]}
      />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">All nominees</h1>
          <p className="page-header__subtitle">
            {editionLabel} &mdash; {uniqueParticipants} nominees across {uniqueCategoryCount}{' '}
            categories
          </p>
        </div>
        <div className="page-header__actions">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={hasNoNominees}
            onClick={() => setIsExportModalOpen(true)}
          >
            <Download size={16} />
            Export CSV
          </button>
          <Link to={`/editions/${editionId}/categories`} className="btn btn-secondary">
            <Users size={16} />
            View categories
          </Link>
        </div>
      </div>

      {!hasNoNominees && (
        <TableFilters
          compact
          rows={[
            {
              items: [
                {
                  type: 'search',
                  id: 'nominee-search',
                  label: 'Search',
                  placeholder: 'Search by nominee name...',
                  value: nameSearch,
                  onChange: handleNameSearchChange,
                },
                {
                  type: 'select',
                  id: 'category-filter',
                  label: 'Category',
                  value: selectedCategoryId,
                  options: [
                    { value: ALL_CATEGORIES, label: 'All categories' },
                    ...availableCategories.map((cat) => ({ value: cat.id, label: cat.name })),
                  ],
                  onChange: handleCategoryChange,
                  narrow: true,
                },
              ],
            },
          ]}
        />
      )}

      {hasNoNominees ? (
        <div className="empty-state">
          <div className="empty-state__icon">
            <Award size={40} />
          </div>
          <h3 className="empty-state__title">No nominees yet</h3>
          <p>There are no nominees for this edition. Start by adding categories and nominees.</p>
        </div>
      ) : (
        <>
          <div className="edition-nominees-grid">
            {paginatedNominees.map((group) => {
              const fullName = [group.name, group.surname].filter(Boolean).join(' ');

              return (
                <div className="edition-nominee-card" key={group.participantId}>
                  <div className="edition-nominee-card__header">
                    {group.photoUrl ? (
                      <img
                        src={group.photoUrl}
                        alt={fullName}
                        className="edition-nominee-card__avatar"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div
                        className="edition-nominee-card__avatar"
                        style={{ backgroundColor: getAvatarColor(fullName) }}
                      >
                        {getInitials(fullName)}
                      </div>
                    )}
                    <div className="edition-nominee-card__info">
                      <h3 className="edition-nominee-card__name">{fullName}</h3>
                      <div className="edition-nominee-card__meta">
                        {group.city && (
                          <span className="edition-nominee-card__city">
                            <MapPin size={13} />
                            {group.city}
                          </span>
                        )}
                        <span className="edition-nominee-card__dp-badge">
                          {group.totalVotes} votes
                        </span>
                      </div>
                    </div>
                    {group.bestRanking !== null && (
                      <span
                        className={`edition-nominee__pos edition-nominee__pos--lg ${getPositionBadge(group.bestRanking)}`}
                      >
                        #{group.bestRanking}
                      </span>
                    )}
                  </div>

                  <div className="edition-nominee-card__categories">
                    <div className="edition-nominee-card__categories-title">
                      <Award size={14} />
                      Nominated in {group.categories.length}{' '}
                      {group.categories.length === 1 ? 'category' : 'categories'}
                    </div>
                    <div className="edition-nominee-card__category-list">
                      {group.categories.map((cat) => (
                        <Link
                          key={cat.editionCategoryId}
                          to={`/editions/${editionId}/categories/${cat.categoryId}/nominees`}
                          className="edition-nominee-card__category-item"
                        >
                          {cat.ranking !== null && (
                            <span
                              className={`edition-nominee__pos ${getPositionBadge(cat.ranking)}`}
                            >
                              #{cat.ranking}
                            </span>
                          )}
                          <span className="edition-nominee-card__category-name">
                            {cat.categoryName}
                          </span>
                          <span className="edition-nominee-card__category-votes">
                            {cat.voteCount} votes
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="edition-nominee-card__footer">
                    {group.profileUrl && (
                      <a
                        href={group.profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-ghost btn-sm"
                      >
                        <ExternalLink size={14} />
                        View profile
                      </a>
                    )}
                    {isAdmin && (
                      <div className="edition-nominee-card__actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          data-tooltip="Edit nominee"
                          aria-label="Edit nominee"
                          onClick={() => setEditingNominee(group.sourceNominee)}
                        >
                          <Pencil size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <span className="pagination__info">
                {startIndex}&ndash;{endIndex} of {uniqueParticipants}
              </span>

              <div className="pagination__controls">
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>

                {getPageRange(currentPage, totalPages).map((page) =>
                  page < 0 ? (
                    <span key={page} className="pagination__ellipsis">
                      &hellip;
                    </span>
                  ) : (
                    <button
                      key={page}
                      type="button"
                      className={`pagination__btn${page === currentPage ? ' pagination__btn--active' : ''}`}
                      onClick={() => setCurrentPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <EditNomineeModal
        isOpen={editingNominee !== null}
        onClose={() => {
          setEditingNominee(null);
          refetch();
        }}
        nominee={editingNominee}
      />

      <Modal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export nominees"
        footer={
          <>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setIsExportModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={isExporting}
              onClick={() => void handleExport()}
            >
              <Download size={16} />
              {isExporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </>
        }
      >
        <div className="export-options">
          <div className="export-options__group">
            <span className="export-options__label">Nominee type</span>
            <div className="export-segmented" role="group" aria-label="Nominee type">
              <button
                type="button"
                className={`export-segmented__option${
                  exportNomineeType === 'all' ? ' export-segmented__option--active' : ''
                }`}
                onClick={() => setExportNomineeType('all')}
              >
                <span className="export-segmented__icon">
                  <ListFilter size={16} />
                </span>
                <span>All</span>
              </button>
              <button
                type="button"
                className={`export-segmented__option${
                  exportNomineeType === 'doctor' ? ' export-segmented__option--active' : ''
                }`}
                onClick={() => setExportNomineeType('doctor')}
              >
                <span className="export-segmented__icon">
                  <Stethoscope size={16} />
                </span>
                <span>Doctors</span>
              </button>
              <button
                type="button"
                className={`export-segmented__option${
                  exportNomineeType === 'clinic' ? ' export-segmented__option--active' : ''
                }`}
                onClick={() => setExportNomineeType('clinic')}
              >
                <span className="export-segmented__icon">
                  <Building2 size={16} />
                </span>
                <span>Clinics</span>
              </button>
            </div>
          </div>

          <div className="export-options__group">
            <span className="export-options__label">Voting status</span>
            <div className="export-segmented" role="group" aria-label="Voting status">
              <button
                type="button"
                className={`export-segmented__option${
                  exportVotedStatus === 'all' ? ' export-segmented__option--active' : ''
                }`}
                onClick={() => setExportVotedStatus('all')}
              >
                <span className="export-segmented__icon">
                  <ListFilter size={16} />
                </span>
                <span>All</span>
              </button>
              <button
                type="button"
                className={`export-segmented__option${
                  exportVotedStatus === 'voted' ? ' export-segmented__option--active' : ''
                }`}
                onClick={() => setExportVotedStatus('voted')}
              >
                <span className="export-segmented__icon">
                  <CheckCircle2 size={16} />
                </span>
                <span>Has voted</span>
              </button>
              <button
                type="button"
                className={`export-segmented__option${
                  exportVotedStatus === 'not-voted' ? ' export-segmented__option--active' : ''
                }`}
                onClick={() => setExportVotedStatus('not-voted')}
              >
                <span className="export-segmented__icon">
                  <CircleSlash size={16} />
                </span>
                <span>Not voted</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
